/**
 * Fetch function for Jira issue → PR hydration.
 *
 * Mirrors the pattern of fetchJiraFieldsQuery in ./index.tsx:
 * - Uses fetchAgg to call AGG
 * - Wraps with inMemoryCache for deduplication
 * - Parses response into IssueLinkedPRs mapping
 */
import { type AggResponse } from '../../../../../common/utils/fetch-agg';

import { PR_LIMIT_PER_ISSUE } from './pr-query-generator';
import {
	type DevOpsReviewer,
	type HydratedPullRequest,
	type ParsedIssueLinkedPRs,
	type JiraPRHydrationData,
} from './pr-types';

/**
 * Parses the raw AGG response into a clean IssueLinkedPRs mapping.
 *
 * Maps each issue ARI to its array of hydrated pull requests.
 *
 * @param response - Raw AGG response from fetchJiraPRsQuery
 * @param issueAris - The original issue ARIs (in order) used to map aliases back
 * @returns Mapping from issue ARI → HydratedPullRequest[]
 */
export const parseJiraPRResponse = (
	response: AggResponse<JiraPRHydrationData>,
	issueAris: string[],
): ParsedIssueLinkedPRs => {
	const result: ParsedIssueLinkedPRs = {};
	const graphStore = response.data?.graphStore;

	if (!graphStore) {
		return result;
	}

	issueAris.forEach((ari, index) => {
		const aliasKey = `issue${index}`;
		const connection = graphStore[aliasKey];
		const prs: HydratedPullRequest[] = [];
		let totalEdgeCount = 0;

		if (connection?.edges) {
			totalEdgeCount = connection.edges.length;
			for (const edge of connection.edges) {
				if (edge?.node) {
					prs.push({
						id: edge.node.id,
						title: edge.node.title,
						url: edge.node.url,
						pullRequestInternalId: edge.node.pullRequestInternalId,
						status: edge.node.status,
						lastUpdated: edge.node.lastUpdated,
						reviewers: (edge.node.reviewers ?? []).filter(
							(r): r is DevOpsReviewer => r?.approvalStatus != null,
						),
					});
				}
			}
		}

		// If the API returned its maximum (PR_LIMIT_PER_ISSUE), there may be
		// more PRs we haven't seen, so we can't show an exact count.
		const hitApiLimit = totalEdgeCount >= PR_LIMIT_PER_ISSUE;

		result[ari] = {
			status: 'loaded',
			prs,
			hasMore: hitApiLimit,
		};
	});

	return result;
};
